import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import HealthScoreOps from './components/HealthScoreOps';
import DatabaseMonitoring from './components/DatabaseMonitoring';
// removed standalone verification tab; review happens inside Doctors tab
import AdminManagement from './components/AdminManagement';
import AdminPasswordReset from './components/AdminPasswordReset';
import SystemHolidays from './components/SystemHolidays';
import VersionManagement from './components/VersionManagement';
import ScheduledJobs from './components/ScheduledJobs';
import LLMPricing from './components/LLMPricing';
import LLMUsage from './components/LLMUsage';
import { Header, Footer } from '../../shared/components';

// API base URL: use env override if provided, otherwise use relative URLs (same origin)
const API_BASE = (process.env.REACT_APP_API_BASE_URL || '');
const apiUrl = (path: string) => `${API_BASE}${path}`;

const getUserStatusLabel = (status?: string | null) => {
  switch ((status || 'not_started').toLowerCase()) {
    case 'pending_verification':
      return 'Pending verification';
    case 'onboarding':
      return 'Onboarding';
    case 'email_verified':
      return 'Email verified';
    case 'incomplete':
      return 'Incomplete';
    case 'completed':
      return 'Completed';
    case 'not_started':
    default:
      return 'Not started';
  }
};

const getUserStatusClass = (status?: string | null) => {
  switch ((status || 'not_started').toLowerCase()) {
    case 'pending_verification':
      return 'pending';
    case 'onboarding':
      return 'info';
    case 'email_verified':
    case 'completed':
      return 'success';
    case 'incomplete':
      return 'warning';
    default:
      return 'pending';
  }
};


interface AuthState {
  isAuthenticated: boolean;
  token: string | null;
  csrfToken: string | null;
  user: any | null;
  isSuperAdmin?: boolean;
}

interface SystemHealth {
  status: string;
  status_color: string;
  health_score: number;
  issues: string[];
  current_metrics: {
    cpu_percent: number;
    memory_percent: number;
    disk_percent: number;
    memory_gb: number;
    memory_total_gb: number;
    disk_gb: number;
    disk_total_gb: number;
  };
  recent_requests: number;
  error_rate: number;
  avg_response_time_ms: number;
}

interface PerformanceOverview {
  overview: {
    requests_per_hour: number;
    avg_response_time_ms: number;
    p95_response_time_ms: number;
    error_rate: number;
    error_count: number;
  };
  endpoint_stats: Array<{
    endpoint: string;
    request_count: number;
    avg_response_time_ms: number;
    error_rate: number;
    methods: string[];
  }>;
}

// Feedback types
interface FeedbackItem {
  id: string;
  user_id?: number | null;
  submitter_role?: string | null; // "user" | "doctor"
  submitter_name?: string | null;
  s3_key: string;
  category?: string | null;
  description?: string | null;
  route?: string | null;
  app_version?: string | null;
  build_number?: string | null;
  platform?: string | null;
  os_version?: string | null;
  device_model?: string | null;
  app_identifier?: string | null;
  status: string;
  closed_date?: string | null;
  extra?: Record<string, any> | null;
  created_at?: string;
  updated_at?: string;
}

function App() {
  const [activeTab, setActiveTab] = useState('system');
  // Users management state
  const [users, setUsers] = useState<Array<any>>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());
  const [selectAllUsers, setSelectAllUsers] = useState(false);
  // Doctors management state
  const [doctors, setDoctors] = useState<Array<any>>([]);
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  const [selectedDoctorIds, setSelectedDoctorIds] = useState<Set<number>>(new Set());
  const [selectAllDoctors, setSelectAllDoctors] = useState(false);
  // Doctor Review page state
  const [reviewDoctorId, setReviewDoctorId] = useState<number | null>(null);
  const [reviewDetails, setReviewDetails] = useState<any | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewFeedback, setReviewFeedback] = useState('');
  const [reviewActionLoading, setReviewActionLoading] = useState<string | null>(null);
  const reviewPanelRef = useRef<HTMLDivElement | null>(null);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [performanceOverview, setPerformanceOverview] = useState<PerformanceOverview | null>(null);
  const [auth, setAuth] = useState<AuthState>({
    isAuthenticated: false,
    token: null,
    csrfToken: null,
    user: null,
    isSuperAdmin: false
  });
  const [loginForm, setLoginForm] = useState({
    username: '',
    password: ''
  });
  const [loginMode, setLoginMode] = useState<'password' | 'otp' | 'otp-enter' | 'forgot-password'>('password');
  const [otpCode, setOtpCode] = useState('');
  const [otpEmail, setOtpEmail] = useState('');
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const [isRequestingPasswordReset, setIsRequestingPasswordReset] = useState(false);
  const [isRequestingOtp, setIsRequestingOtp] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isRefreshing, setIsRefreshing] = useState({
    system: false,
    app: false
  });
  const [globalNotice, setGlobalNotice] = useState<{ type: 'session' | 'service' | 'network'; message: string } | null>(null);
  const isLogoutInProgressRef = useRef(false);
  const refreshPromiseRef = useRef<Promise<string | null> | null>(null);

  // Check if we're on the password reset page
  const urlParams = new URLSearchParams(window.location.search);
  const resetToken = urlParams.get('token');
  const [showPasswordReset, setShowPasswordReset] = useState(!!resetToken);

  // Feedback state
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackItem | null>(null);
  const [selectedFeedbackImageUrl, setSelectedFeedbackImageUrl] = useState<string | null>(null);
  const [tempStatus, setTempStatus] = useState<string>('');
  const [updatingFeedback, setUpdatingFeedback] = useState<string | null>(null);
  const [feedbackFilters, setFeedbackFilters] = useState({
    role: 'all' as 'all' | 'user' | 'doctor',
    status: 'all' as 'all' | 'open' | 'in_progress' | 'resolved',
    category: 'all' as string,
    search: '' as string,
    startDate: '' as string,
    endDate: '' as string,
  });

  // Check for existing token on app load
  // Helper function to fetch current admin info (including isSuperAdmin flag)
  const fetchAdminInfo = async (token: string) => {
    try {
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
      
      const response = await safeFetch(apiUrl('/api/v1/admin/admins/me'), {
        headers: headers
      });
      if (response.ok) {
        const adminData = await response.json();
        console.log('Admin info fetched:', adminData);
        return adminData.is_superadmin || false;
      } else {
        console.error('Failed to fetch admin info, status:', response.status);
      }
    } catch (error) {
      console.error('Failed to fetch admin info:', error);
    }
    return false;
  };

  // Function to refresh CSRF token
  const refreshCsrfToken = async (token: string) => {
    try {
      const response = await safeFetch(apiUrl('/api/v1/auth/admin/csrf/refresh'), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const newCsrfToken = data.csrf_token;
        if (newCsrfToken) {
          localStorage.setItem('dashboard_csrf_token', newCsrfToken);
          setAuth(prev => ({ ...prev, csrfToken: newCsrfToken }));
          return newCsrfToken;
        }
      } else {
        console.warn('Failed to refresh CSRF token:', response.status);
      }
    } catch (error) {
      console.error('Error refreshing CSRF token:', error);
    }
    return null;
  };

  useEffect(() => {
    const token = localStorage.getItem('dashboard_token');
    const user = localStorage.getItem('dashboard_user');
    const isSuperAdmin = localStorage.getItem('dashboard_is_superadmin') === 'true';
    const csrfToken = localStorage.getItem('dashboard_csrf_token');
    
    if (token && user) {
      setAuth({
        isAuthenticated: true,
        token,
        csrfToken: csrfToken || null, // restore persisted CSRF token temporarily
        user: JSON.parse(user),
        isSuperAdmin
      });
      
      // Re-fetch admin info to ensure it's up to date
      fetchAdminInfo(token).then(superAdmin => {
        if (superAdmin !== isSuperAdmin) {
          localStorage.setItem('dashboard_is_superadmin', String(superAdmin));
          setAuth(prev => ({ ...prev, isSuperAdmin: superAdmin }));
        }
      });
      
      // Refresh CSRF token on page load/refresh
      refreshCsrfToken(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount to restore session and refresh tokens
  }, []);

  const login = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      if (loginMode === 'password') {
        const payload = { email: loginForm.username, password: loginForm.password };
        
        const response = await safeFetch(apiUrl('/api/v1/auth/admin/login'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload)
        });
        
        if (response.ok) {
          const data = await response.json();
          
          // Fetch admin info to check if super admin
          const isSuperAdmin = await fetchAdminInfo(data.access_token);
          
          const authState = {
            isAuthenticated: true,
            token: data.access_token,
            csrfToken: data.csrf_token || null,
            user: data.admin || { username: loginForm.username },
            isSuperAdmin
          };
          
          setAuth(authState);
          localStorage.setItem('dashboard_token', data.access_token);
          if (data.refresh_token) {
            localStorage.setItem('dashboard_refresh_token', data.refresh_token);
          } else {
            localStorage.removeItem('dashboard_refresh_token');
          }
          localStorage.setItem('dashboard_user', JSON.stringify(authState.user));
          localStorage.setItem('dashboard_is_superadmin', String(isSuperAdmin));
          if (data.csrf_token) {
            localStorage.setItem('dashboard_csrf_token', data.csrf_token);
          } else {
            localStorage.removeItem('dashboard_csrf_token');
          }
          // Note: CSRF token stored in React state (memory), not localStorage
          
          setLoginForm({ username: '', password: '' });
        } else {
          const errorData = await response.json().catch(() => ({}));
          // Handle FastAPI validation errors (detail is an array)
          let errorMessage = 'Admin login failed';
          if (errorData.detail) {
            if (Array.isArray(errorData.detail)) {
              // Validation errors: extract first error message
              errorMessage = errorData.detail[0]?.msg || errorMessage;
            } else if (typeof errorData.detail === 'string') {
              errorMessage = errorData.detail;
            }
          }
          setLoginError(errorMessage);
        }
      } else if (loginMode === 'otp-enter') {
        // Admin OTP verify - use otpEmail directly (not loginForm.username)
        if (!otpEmail) {
          setLoginError('Email is required. Please request OTP again.');
          return;
        }
        const payload = { email: otpEmail, code: otpCode };
        
        const response = await safeFetch(apiUrl('/api/v1/auth/admin/otp/verify'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload)
        });
        
        if (response.ok) {
          const data = await response.json();
          
          // Fetch admin info to check if super admin
          const isSuperAdmin = await fetchAdminInfo(data.access_token);
          
          const authState = {
            isAuthenticated: true,
            token: data.access_token,
            csrfToken: data.csrf_token || null,
            user: data.admin || { username: otpEmail },
            isSuperAdmin
          };
          setAuth(authState);
          localStorage.setItem('dashboard_token', data.access_token);
          if (data.refresh_token) {
            localStorage.setItem('dashboard_refresh_token', data.refresh_token);
          } else {
            localStorage.removeItem('dashboard_refresh_token');
          }
          localStorage.setItem('dashboard_user', JSON.stringify(authState.user));
          localStorage.setItem('dashboard_is_superadmin', String(isSuperAdmin));
          if (data.csrf_token) {
            localStorage.setItem('dashboard_csrf_token', data.csrf_token);
          } else {
            localStorage.removeItem('dashboard_csrf_token');
          }
          // Note: CSRF token stored in React state (memory), not localStorage
          setLoginForm({ username: '', password: '' });
          setOtpCode('');
          setOtpEmail('');
        } else {
          const errorData = await response.json().catch(() => ({}));
          // Handle FastAPI validation errors (detail is an array)
          let errorMessage = 'OTP verification failed';
          if (errorData.detail) {
            if (Array.isArray(errorData.detail)) {
              // Validation errors: extract first error message
              errorMessage = errorData.detail[0]?.msg || errorMessage;
            } else if (typeof errorData.detail === 'string') {
              errorMessage = errorData.detail;
            }
          }
          setLoginError(errorMessage);
        }
      }
    } catch (error) {
      setLoginError('Network error. Please try again.');
      console.error('Login error:', error);
    }
  };

  const requestOtp = async (email: string) => {
    setLoginError('');
    setIsRequestingOtp(true);
    try {
      const payload = { email };
      
      const response = await safeFetch(apiUrl('/api/v1/auth/admin/otp/request'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        await response.json();
        setOtpEmail(email);
        setLoginMode('otp-enter');
        // Show success message without alert
        setLoginError('');
      } else {
        const errorData = await response.json().catch(() => ({}));
        let errorMessage = 'Failed to request OTP';
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            errorMessage = errorData.detail[0]?.msg || errorMessage;
          } else if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          }
        }
        setLoginError(errorMessage);
      }
    } catch (e) {
      setLoginError('Network error requesting OTP');
    } finally {
      setIsRequestingOtp(false);
    }
  };

  const requestPasswordReset = async (email: string) => {
    setForgotPasswordMessage('');
    setLoginError('');
    setIsRequestingPasswordReset(true);
    try {
      const payload = { email };
      
      const response = await safeFetch(apiUrl('/api/v1/auth/admin/password/forgot'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const data = await response.json();
        setForgotPasswordMessage(data.message || 'If this email is registered as an admin, a password reset link has been sent.');
      } else {
        await response.json().catch(() => ({}));
        // Still show success message for security (don't reveal if admin exists)
        setForgotPasswordMessage('If this email is registered as an admin, a password reset link has been sent.');
      }
    } catch (e) {
      setLoginError('Network error requesting password reset');
    } finally {
      setIsRequestingPasswordReset(false);
    }
  };

  const logout = (message?: string) => {
    if (isLogoutInProgressRef.current) return;
    isLogoutInProgressRef.current = true;
    const refreshToken = localStorage.getItem('dashboard_refresh_token');
    if (refreshToken) {
      fetch(apiUrl('/api/v1/auth/logout'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      }).catch(() => {
        // ignore best-effort revoke failures
      });
    }
    setAuth({
      isAuthenticated: false,
      token: null,
      csrfToken: null,
      user: null,
      isSuperAdmin: false
    });
    localStorage.removeItem('dashboard_token');
    localStorage.removeItem('dashboard_refresh_token');
    localStorage.removeItem('dashboard_user');
    localStorage.removeItem('dashboard_is_superadmin');
    localStorage.removeItem('dashboard_csrf_token');
    // Reset login mode to password login
    setLoginMode('password');
    setOtpEmail('');
    setOtpCode('');
    setLoginForm({ username: '', password: '' });
    setLoginError(message || '');
    setGlobalNotice(null);
    setTimeout(() => {
      isLogoutInProgressRef.current = false;
    }, 500);
  };

  const refreshAdminAccessToken = async (): Promise<string | null> => {
    if (refreshPromiseRef.current) return refreshPromiseRef.current;

    refreshPromiseRef.current = (async () => {
      const refreshToken = localStorage.getItem('dashboard_refresh_token');
      if (!refreshToken) return null;

      try {
        const resp = await fetch(apiUrl('/api/v1/auth/admin/refresh'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });

        if (!resp.ok) return null;
        const data = await resp.json();
        if (!data?.access_token) return null;

        localStorage.setItem('dashboard_token', data.access_token);
        if (data.csrf_token) {
          localStorage.setItem('dashboard_csrf_token', data.csrf_token);
        } else {
          localStorage.removeItem('dashboard_csrf_token');
        }
        if (data.refresh_token) {
          localStorage.setItem('dashboard_refresh_token', data.refresh_token);
        }
        setAuth((prev) => ({
          ...prev,
          isAuthenticated: true,
          token: data.access_token,
          csrfToken: data.csrf_token || prev.csrfToken || null,
        }));
        return data.access_token as string;
      } catch {
        return null;
      } finally {
        refreshPromiseRef.current = null;
      }
    })();

    return refreshPromiseRef.current;
  };

  // Global fetch wrapper with auth/network/service classification.
  const safeFetch = async (input: RequestInfo, init?: RequestInit) => {
    const url = typeof input === 'string' ? input : input instanceof Request ? input.url : '';
    const isAuthEndpoint = url.includes('/api/v1/auth/');
    const requestHeaders = new Headers(init?.headers || {});
    const alreadyRetried = requestHeaders.get('X-Auth-Retry') === '1';
    try {
      const response = await fetch(input, init);

      if (response.status === 503) {
        setGlobalNotice({
          type: 'service',
          message: 'Service unavailable. Please try again.',
        });
        return response;
      }

      if (response.status === 401 || response.status === 403) {
        if (response.status === 401 && auth.isAuthenticated && !isAuthEndpoint && !alreadyRetried) {
          const newToken = await refreshAdminAccessToken();
          if (newToken) {
            const retryHeaders = new Headers(init?.headers || {});
            retryHeaders.set('Authorization', `Bearer ${newToken}`);
            const csrf = localStorage.getItem('dashboard_csrf_token');
            if (csrf) retryHeaders.set('X-CSRF-Token', csrf);
            retryHeaders.set('X-Auth-Retry', '1');
            return fetch(input, { ...init, headers: retryHeaders });
          }
        }
        if (auth.isAuthenticated && !isAuthEndpoint) {
          logout(
            response.status === 401
              ? 'Your session has expired. Please log in again.'
              : "You don't have permission to access this resource."
          );
        }
        return response;
      }

      if (response.status === 502 || response.status === 504) {
        setGlobalNotice({
          type: 'network',
          message: 'Unable to reach the server right now. Please try again.',
        });
        return response;
      }

      return response;
    } catch (error) {
      console.error('Network request failed:', error);
      const offline = typeof navigator !== 'undefined' && !navigator.onLine;
      setGlobalNotice({
        type: 'network',
        message: offline
          ? "You're offline. Please check your connection and try again."
          : 'Unable to reach the server right now. Please try again.',
      });
      throw error;
    }
  };

  const getAuthHeaders = (payload?: any, method: string = 'GET'): Record<string, string> => {
    const headers: Record<string, string> = {};
    if (auth.token) {
      headers['Authorization'] = `Bearer ${auth.token}`;
      headers['Content-Type'] = 'application/json';
    }

    // Add CSRF token when available (safe for GET too; avoids accidental omission)
    if (auth.csrfToken) {
      headers['X-CSRF-Token'] = auth.csrfToken;
    }

    return headers;
  };

  const fetchFeedback = async () => {
    if (!auth.isAuthenticated) return;
    setIsLoadingFeedback(true);
    try {
      const response = await safeFetch(apiUrl('/api/v1/feedback'), { method: 'GET', headers: getAuthHeaders() });
      if (response.status === 401 || response.status === 403) {
        console.log('Authentication failed for feedback, logging out...');
        logout();
        return;
      }
      const data = await response.json();
      const items: FeedbackItem[] = Array.isArray(data) ? data : (data.items || []);
      // Normalize datetimes to ISO strings
      const normalized = items.map(it => ({
        ...it,
        created_at: (it as any).created_at ? new Date((it as any).created_at as any).toISOString() : undefined,
        updated_at: (it as any).updated_at ? new Date((it as any).updated_at as any).toISOString() : undefined,
      }));
      setFeedback(normalized);
    } catch (e) {
      console.error('Error fetching feedback:', e);
      setFeedback([]);
    } finally {
      setIsLoadingFeedback(false);
    }
  };

  const viewScreenshot = async (feedbackId: string) => {
    try {
      const resp = await safeFetch(apiUrl(`/api/v1/feedback/${feedbackId}/view-url`), { headers: getAuthHeaders() });
      if (!resp.ok) return;
      const data = await resp.json();
      const url = data.viewUrl;
      if (url) window.open(url, '_blank');
    } catch (e) {
      console.error('Failed to get view URL:', e);
    }
  };

  const updateFeedbackStatus = async (feedbackId: string, status: string, closedDate?: string) => {
    console.log('Updating feedback status:', { feedbackId, status, closedDate });
    setUpdatingFeedback(feedbackId);
    try {
      const payload: { status: string; closed_date?: string } = { status };
      if (closedDate) {
        payload.closed_date = closedDate;
      }
      
      console.log('Sending payload:', payload);
      console.log('API URL:', apiUrl(`/api/v1/feedback/${feedbackId}`));
      
      const response = await safeFetch(apiUrl(`/api/v1/feedback/${feedbackId}`), {
        method: 'PATCH',
        headers: getAuthHeaders(payload, 'PATCH'),
        body: JSON.stringify(payload)
      });
      
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      if (response.ok) {
        const updatedFeedback = await response.json();
        console.log('Updated feedback received:', updatedFeedback);
        setFeedback(prev => prev.map(fb => fb.id === feedbackId ? updatedFeedback : fb));
        if (selectedFeedback && selectedFeedback.id === feedbackId) {
          setSelectedFeedback(updatedFeedback);
        }
        setTempStatus('');
        console.log('Feedback updated successfully');
        alert('Status updated successfully!');
      } else {
        const errorText = await response.text();
        console.error('Failed to update feedback status:', response.status, errorText);
        alert('Failed to update feedback status. Please try again.');
      }
    } catch (e) {
      console.error('Error updating feedback status:', e);
      alert('Error updating feedback status. Please try again.');
    } finally {
      setUpdatingFeedback(null);
    }
  };

  const deleteFeedback = async (feedbackId: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to delete this feedback?\n\n' +
      'This will permanently delete:\n' +
      '- The feedback entry from the database\n' +
      '- The screenshot from S3\n\n' +
      'This action cannot be undone.'
    );
    
    if (!confirmed) return;
    
    try {
      const response = await safeFetch(apiUrl(`/api/v1/feedback/${feedbackId}`), {
        method: 'DELETE',
        headers: getAuthHeaders(undefined, 'DELETE')
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Feedback deleted:', result);
        // Remove from local state
        setFeedback(prev => prev.filter(fb => fb.id !== feedbackId));
        alert(result.message || 'Feedback deleted successfully');
      } else {
        const errorText = await response.text();
        console.error('Failed to delete feedback:', response.status, errorText);
        alert('Failed to delete feedback. Please try again.');
      }
    } catch (e) {
      console.error('Error deleting feedback:', e);
      alert('Error deleting feedback. Please try again.');
    }
  };


  const fetchSystemHealth = async () => {
    setIsRefreshing(prev => ({ ...prev, system: true }));
    try {
      const response = await safeFetch(apiUrl('/api/v1/dashboard/system-health'), {
        headers: getAuthHeaders()
      });
      
      if (response.status === 401 || response.status === 403) {
        console.log('System health unauthorized; logging out.');
        logout();
        return;
      }
      
      const data = await response.json();
      setSystemHealth(data);
    } catch (error) {
      console.error('Error fetching system health:', error);
    } finally {
      setIsRefreshing(prev => ({ ...prev, system: false }));
    }
  };

  const fetchUsers = async () => {
    if (!auth.isAuthenticated) return;
    setUsersLoading(true);
    try {
      const resp = await safeFetch(apiUrl('/api/v1/admin/users?limit=500'), { headers: getAuthHeaders() });
      if (resp.status === 401 || resp.status === 403) {
        logout();
        return;
      }
      const data = await resp.json();
      setUsers(Array.isArray(data) ? data : []);
      // Reset selection when reloading
      setSelectedUserIds(new Set());
      setSelectAllUsers(false);
    } catch (e) {
      console.error('Failed to load users', e);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchDoctors = async () => {
    if (!auth.isAuthenticated) return;
    setDoctorsLoading(true);
    try {
      const resp = await safeFetch(apiUrl('/api/v1/admin/doctors?limit=500'), { headers: getAuthHeaders() });
      if (resp.status === 401 || resp.status === 403) {
        logout();
        return;
      }
      const data = await resp.json();
      setDoctors(Array.isArray(data) ? data : []);
      // Reset selection when reloading
      setSelectedDoctorIds(new Set());
      setSelectAllDoctors(false);
    } catch (e) {
      console.error('Failed to load doctors', e);
      setDoctors([]);
    } finally {
      setDoctorsLoading(false);
    }
  };

  const openDoctorReview = async (doctorId: number) => {
    setReviewDoctorId(doctorId);
    setReviewLoading(true);
    setReviewDetails(null);
    setActiveTab('doctor-review');
    try {
      const resp = await safeFetch(apiUrl(`/api/v1/admin/doctors/${doctorId}/verification`), { headers: getAuthHeaders() });
      const data = await resp.json();
      setReviewDetails(data);
      setTimeout(() => {
        if (reviewPanelRef.current) {
          reviewPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 0);
    } catch (e) {
      console.error('Failed to load doctor verification details', e);
      setReviewDetails(null);
    } finally {
      setReviewLoading(false);
    }
  };

  const approveDoctor = async () => {
    if (!reviewDoctorId) return;
    setReviewActionLoading('approve');
    try {
      const payload: any = {};
      if (reviewNotes) payload.verification_notes = reviewNotes;
      const resp = await safeFetch(apiUrl(`/api/v1/admin/doctors/${reviewDoctorId}/approve`), {
        method: 'POST',
        headers: getAuthHeaders(payload, 'POST'),
        body: JSON.stringify(payload)
      });
      if (!resp.ok) {
        const t = await resp.text();
        alert(`Failed to approve: ${t}`);
        return;
      }
      alert('Doctor approved');
      setReviewDoctorId(null);
      setReviewDetails(null);
      setReviewNotes('');
      await fetchDoctors();
    } catch (e) {
      console.error('Approve failed', e);
    } finally {
      setReviewActionLoading(null);
    }
  };

  const rejectDoctor = async () => {
    if (!reviewDoctorId) return;
    if (!reviewFeedback.trim()) { alert('Enter feedback to reject'); return; }
    setReviewActionLoading('reject');
    try {
      const payload = { rejection_reason: 'profile_changes_required', rejection_details: reviewFeedback };
      const resp = await safeFetch(apiUrl(`/api/v1/admin/doctors/${reviewDoctorId}/reject`), {
        method: 'POST',
        headers: getAuthHeaders(payload, 'POST'),
        body: JSON.stringify(payload)
      });
      if (!resp.ok) {
        const t = await resp.text();
        alert(`Failed to reject: ${t}`);
        return;
      }
      alert('Feedback sent and status set to rejected');
      setReviewDoctorId(null);
      setReviewDetails(null);
      setReviewFeedback('');
      await fetchDoctors();
    } catch (e) {
      console.error('Reject failed', e);
    } finally {
      setReviewActionLoading(null);
    }
  };

  const requestInfoDoctor = async () => {
    if (!reviewDoctorId) return;
    if (!reviewFeedback.trim()) { alert('Enter feedback to request info'); return; }
    setReviewActionLoading('request');
    try {
      const payload = {
        requested_info: reviewFeedback,
        fields_needed: ['documents']
      };
      
      const resp = await safeFetch(apiUrl(`/api/v1/admin/doctors/${reviewDoctorId}/request-info`), {
        method: 'POST',
        headers: getAuthHeaders(payload, 'POST'),
        body: JSON.stringify(payload)
      });
      if (!resp.ok) {
        const t = await resp.text();
        alert(`Failed to request info: ${t}`);
        return;
      }
      alert('Requested additional info');
      setReviewDoctorId(null);
      setReviewDetails(null);
      setReviewFeedback('');
      setReviewNotes('');
      await fetchDoctors();
    } catch (e) {
      console.error('Request info failed', e);
    } finally {
      setReviewActionLoading(null);
    }
  };

  const toggleSelectAllUsers = () => {
    if (selectAllUsers) {
      setSelectedUserIds(new Set());
      setSelectAllUsers(false);
    } else {
      const all = new Set<number>(users.map((u: any) => u.id));
      setSelectedUserIds(all);
      setSelectAllUsers(true);
    }
  };

  const toggleSelectUser = (id: number) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const markUsersForDeletion = async () => {
    const ids = Array.from(selectedUserIds.values());
    if (ids.length === 0) return;
    if (!window.confirm(`Mark ${ids.length} user(s) for deletion?\n\nThis will deactivate their accounts but preserve all data.`)) return;
    try {
      const payload = { user_ids: ids };
      const resp = await safeFetch(apiUrl('/api/v1/admin/users/mark-for-deletion'), {
        method: 'POST',
        headers: getAuthHeaders(payload, 'POST'),
        body: JSON.stringify(payload)
      });
      if (!resp.ok) {
        const text = await resp.text();
        alert(`Failed to mark users for deletion: ${resp.status} ${text}`);
        return;
      }
      const result = await resp.json();
      await fetchUsers();
      setSelectedUserIds(new Set()); // Clear selection
      alert(result.message || 'Users marked for deletion');
    } catch (e) {
      console.error('Mark for deletion failed', e);
      alert('Failed to mark users for deletion');
    }
  };

  const permanentlyDeleteUsers = async () => {
    const ids = Array.from(selectedUserIds.values());
    if (ids.length === 0) return;
    
    // Double confirmation for permanent deletion
    const firstConfirm = window.confirm(
      `⚠️ PERMANENT DELETION WARNING ⚠️\n\n` +
      `You are about to PERMANENTLY DELETE ${ids.length} user(s) and ALL their data:\n` +
      `- User accounts and profiles\n` +
      `- All health data (nutrition, vitals, lab reports)\n` +
      `- All appointments, prescriptions, and chat history\n` +
      `- ALL related records\n\n` +
      `This action is IRREVERSIBLE!\n\n` +
      `Are you sure you want to continue?`
    );
    
    if (!firstConfirm) return;
    
    const secondConfirm = window.confirm(
      `FINAL CONFIRMATION\n\n` +
      `Type or think "DELETE" to confirm.\n\n` +
      `Delete ${ids.length} user(s) PERMANENTLY?`
    );
    
    if (!secondConfirm) return;
    
    try {
      const payload = { user_ids: ids };
      const resp = await safeFetch(apiUrl('/api/v1/admin/users/delete-permanently'), {
        method: 'POST',
        headers: getAuthHeaders(payload, 'POST'),
        body: JSON.stringify(payload)
      });
      if (!resp.ok) {
        const text = await resp.text();
        alert(`Failed to delete users permanently: ${resp.status} ${text}`);
        return;
      }
      const result = await resp.json();
      await fetchUsers();
      setSelectedUserIds(new Set()); // Clear selection
      
      let message = result.message || `Permanently deleted ${result.deleted} user(s)`;
      if (result.errors && result.errors.length > 0) {
        message += '\n\nErrors:\n' + result.errors.join('\n');
      }
      alert(message);
    } catch (e) {
      console.error('Permanent deletion failed', e);
      alert('Failed to permanently delete users');
    }
  };

  const markDoctorsForDeletion = async () => {
    const ids = Array.from(selectedDoctorIds.values());
    if (ids.length === 0) return;
    if (!window.confirm(`Mark ${ids.length} doctor(s) for deletion?\n\nThis will deactivate their accounts but preserve all data.`)) return;
    try {
      const payload = { doctor_ids: ids };
      const resp = await safeFetch(apiUrl('/api/v1/admin/doctors/mark-for-deletion'), {
        method: 'POST',
        headers: getAuthHeaders(payload, 'POST'),
        body: JSON.stringify(payload)
      });
      if (!resp.ok) {
        const text = await resp.text();
        alert(`Failed to mark doctors for deletion: ${resp.status} ${text}`);
        return;
      }
      const result = await resp.json();
      await fetchDoctors();
      setSelectedDoctorIds(new Set());
      alert(result.message || 'Doctors marked for deletion');
    } catch (e) {
      console.error('Mark for deletion failed', e);
      alert('Failed to mark doctors for deletion');
    }
  };

  const permanentlyDeleteDoctors = async () => {
    const ids = Array.from(selectedDoctorIds.values());
    if (ids.length === 0) return;
    const firstConfirm = window.confirm(
      `⚠️ PERMANENT DELETION WARNING ⚠️\n\n` +
      `You are about to PERMANENTLY DELETE ${ids.length} doctor(s) and ALL their data.\n` +
      `This action is IRREVERSIBLE!\n\n` +
      `Are you sure you want to continue?`
    );
    if (!firstConfirm) return;
    const secondConfirm = window.confirm(
      `FINAL CONFIRMATION\n\nDelete ${ids.length} doctor(s) PERMANENTLY?`
    );
    if (!secondConfirm) return;
    try {
      const payload = { doctor_ids: ids };
      const resp = await safeFetch(apiUrl('/api/v1/admin/doctors/delete-permanently'), {
        method: 'POST',
        headers: getAuthHeaders(payload, 'POST'),
        body: JSON.stringify(payload)
      });
      if (!resp.ok) {
        const text = await resp.text();
        alert(`Failed to delete doctors permanently: ${resp.status} ${text}`);
        return;
      }
      const result = await resp.json();
      await fetchDoctors();
      setSelectedDoctorIds(new Set());
      let message = result.message || `Permanently deleted ${result.deleted} doctor(s)`;
      if (result.errors && result.errors.length > 0) {
        message += '\n\nErrors:\n' + result.errors.join('\n');
      }
      alert(message);
    } catch (e) {
      console.error('Permanent deletion failed', e);
      alert('Failed to permanently delete doctors');
    }
  };

  const toggleSelectAllDoctors = () => {
    if (selectAllDoctors) {
      setSelectedDoctorIds(new Set());
      setSelectAllDoctors(false);
    } else {
      const all = new Set<number>(doctors.map((d: any) => d.id));
      setSelectedDoctorIds(all);
      setSelectAllDoctors(true);
    }
  };

  const toggleSelectDoctor = (id: number) => {
    setSelectedDoctorIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const fetchPerformanceOverview = async () => {
    setIsRefreshing(prev => ({ ...prev, app: true }));
    try {
      const response = await safeFetch(apiUrl('/api/v1/dashboard/metrics/overview'), {
        headers: getAuthHeaders()
      });
      
      if (response.status === 401 || response.status === 403) {
        console.log('Performance overview unauthorized; logging out.');
        logout();
        return;
      }
      
      const data = await response.json();
      // Transform the data to match the expected PerformanceOverview interface
      const transformedData = {
        overview: {
          requests_per_hour: data.total_requests || 0,
          avg_response_time_ms: data.avg_response_time || 0,
          p95_response_time_ms: data.avg_response_time * 1.5 || 0, // Approximate p95
          error_rate: data.error_rate || 0,
          error_count: Math.round((data.error_rate || 0) * (data.total_interactions || 0))
        },
        endpoint_stats: [] // We'll need to update this when we have endpoint-specific data
      };
      setPerformanceOverview(transformedData);
    } catch (error) {
      console.error('Error fetching performance overview:', error);
    } finally {
      setIsRefreshing(prev => ({ ...prev, app: false }));
    }
  };


  useEffect(() => {
    if (!auth.isAuthenticated) return;
    
    // Initial fetch
    fetchSystemHealth();
    fetchPerformanceOverview();
    fetchFeedback();
    fetchUsers();
    fetchDoctors();
    
    // Set up polling for real-time updates
    // Only poll when tab is visible to avoid unnecessary requests
    const interval = setInterval(() => {
      // Only poll if tab is visible and still authenticated
      if (!document.hidden && auth.isAuthenticated) {
        fetchSystemHealth();
        fetchPerformanceOverview();
      }
    }, 30000); // Update every 30 seconds
    
    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated]);

  // Fetch screenshot URL when selectedFeedback changes
  useEffect(() => {
    const fetchScreenshotUrl = async () => {
      if (selectedFeedback) {
        try {
          const resp = await safeFetch(apiUrl(`/api/v1/feedback/${selectedFeedback.id}/view-url`), { headers: getAuthHeaders() });
          if (resp.ok) {
            const data = await resp.json();
            setSelectedFeedbackImageUrl(data.viewUrl);
          }
        } catch (e) {
          console.error('Failed to get screenshot URL:', e);
          setSelectedFeedbackImageUrl(null);
        }
      } else {
        setSelectedFeedbackImageUrl(null);
      }
    };
    
    fetchScreenshotUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeedback]);

  // Render System Monitoring Page
  const renderSystemMonitoring = () => (
    <div className="monitoring-section">
      <div className="section-header">
        <div className="section-title">System Performance Monitoring</div>
        <button 
          className={`refresh-button ${isRefreshing.system ? 'refreshing' : ''}`} 
          onClick={fetchSystemHealth} 
          disabled={isRefreshing.system}
          title="Refresh System Metrics"
        >
          {isRefreshing.system ? '⏳' : '🔄'}
        </button>
      </div>
      
      {/* System Metrics */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-number">
            {systemHealth ? `${systemHealth.current_metrics.cpu_percent.toFixed(1)}%` : '--'}
          </div>
          <div className="stat-label">CPU Usage</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">
            {systemHealth ? `${systemHealth.current_metrics.memory_percent.toFixed(1)}%` : '--'}
          </div>
          <div className="stat-label">Memory Usage</div>
          {systemHealth && (
            <div className="stat-detail">
              {systemHealth.current_metrics.memory_gb.toFixed(1)}GB used of {systemHealth.current_metrics.memory_total_gb.toFixed(1)}GB
            </div>
          )}
        </div>
        <div className="stat-card">
          <div className="stat-number">
            {systemHealth ? `${systemHealth.current_metrics.disk_percent.toFixed(1)}%` : '--'}
          </div>
          <div className="stat-label">Disk Usage</div>
          {systemHealth && (
            <div className="stat-detail">
              {systemHealth.current_metrics.disk_gb.toFixed(1)}GB used of {systemHealth.current_metrics.disk_total_gb.toFixed(1)}GB
            </div>
          )}
        </div>
      </div>

      {/* System Issues (if any) */}
      {systemHealth && systemHealth.issues.length > 0 && (
        <div className="system-issues">
          <div className="section-title">System Issues</div>
          <div className="issues-list">
            {systemHealth.issues.map((issue, index) => (
              <div key={index} className="issue-item">
                <span className="issue-icon">⚠️</span>
                <span className="issue-text">{issue}</span>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );

  // Render App Monitoring Page  
  const renderAppMonitoring = () => (
    <div className="monitoring-section">
      <div className="section-header">
        <div className="section-title">Application Performance Monitoring</div>
        <button 
          className={`refresh-button ${isRefreshing.app ? 'refreshing' : ''}`} 
          onClick={fetchPerformanceOverview} 
          disabled={isRefreshing.app}
          title="Refresh App Metrics"
        >
          {isRefreshing.app ? '⏳' : '🔄'}
        </button>
      </div>
      
      {performanceOverview && (
        <>
          {/* App Overview Metrics */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">{performanceOverview.endpoint_stats.length}</div>
              <div className="stat-label">Active Endpoints</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{performanceOverview.overview.error_count}</div>
              <div className="stat-label">Total Errors</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{(performanceOverview.overview.error_rate * 100).toFixed(2)}%</div>
              <div className="stat-label">Error Rate</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{performanceOverview.overview.requests_per_hour.toFixed(1)}</div>
              <div className="stat-label">Requests/Hour</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{performanceOverview.overview.avg_response_time_ms.toFixed(0)}ms</div>
              <div className="stat-label">Avg Response Time</div>
              <div className="stat-detail">
                P95: {performanceOverview.overview.p95_response_time_ms.toFixed(0)}ms
              </div>
            </div>
          </div>

          {/* Top Endpoints Table */}
          <div className="section-title">Top Endpoints Performance</div>
          <div className="endpoints-table">
            <div className="table-header">
              <div>Endpoint</div>
              <div>Requests</div>
              <div>Avg Time</div>
              <div>Error Rate</div>
              <div>Methods</div>
            </div>
            {performanceOverview.endpoint_stats.map((endpoint, index) => (
              <div key={index} className="table-row">
                <div className="endpoint-path">{endpoint.endpoint}</div>
                <div>{endpoint.request_count}</div>
                <div>{endpoint.avg_response_time_ms.toFixed(0)}ms</div>
                <div className={endpoint.error_rate > 0.05 ? 'error-rate-high' : 'error-rate-low'}>
                  {(endpoint.error_rate * 100).toFixed(1)}%
                </div>
                <div className="methods">{endpoint.methods.join(', ')}</div>
              </div>
            ))}
          </div>
        </>
      )}

    </div>
  );


  // Render Feedback Detail View
  const renderFeedbackDetail = () => {
    if (!selectedFeedback) return null;

    return (
      <div className="monitoring-section">
        <div className="section-header">
          <div className="section-title">Feedback Details</div>
          <button 
            className="view-workflow-btn"
            onClick={() => {
              setSelectedFeedback(null);
              setTempStatus('');
            }}
            style={{ background: '#6c757d' }}
          >
            ← Back to List
          </button>
        </div>

        <div className="feedback-detail-container">
          {/* Screenshot Section */}
          <div className="feedback-detail-section">
            <h3>Screenshot</h3>
            <div className="screenshot-container">
              {selectedFeedbackImageUrl ? (
                <img 
                  src={selectedFeedbackImageUrl}
                  alt="Feedback Screenshot"
                  style={{ maxWidth: '100%', border: '1px solid #e6e8ea', borderRadius: '8px' }}
                  onError={(e) => {
                    // If image fails to load, hide it
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              ) : (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  Loading screenshot...
                </div>
              )}
              <button 
                className="view-workflow-btn"
                onClick={() => viewScreenshot(selectedFeedback.id)}
                style={{ marginTop: '12px' }}
              >
                Open in New Tab
              </button>
            </div>
          </div>

          {/* Details Section */}
          <div className="feedback-detail-section">
            <h3>Information</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Submitted By:</span>
                <span className="detail-value">{selectedFeedback.submitter_name || selectedFeedback.user_id || '-'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Role:</span>
                <span className="detail-value">
                  <span className={`status-badge ${selectedFeedback.submitter_role === 'doctor' ? 'pending' : 'success'}`}>
                    {selectedFeedback.submitter_role || 'user'}
                  </span>
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Category:</span>
                <span className="detail-value">{selectedFeedback.category || '-'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">App:</span>
                <span className="detail-value">{selectedFeedback.app_identifier || '-'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Platform:</span>
                <span className="detail-value">{selectedFeedback.platform || '-'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">OS Version:</span>
                <span className="detail-value">{selectedFeedback.os_version || '-'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Device:</span>
                <span className="detail-value">{selectedFeedback.device_model || '-'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">App Version:</span>
                <span className="detail-value">{selectedFeedback.app_version || '-'} ({selectedFeedback.build_number || '-'})</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Route:</span>
                <span className="detail-value">{selectedFeedback.route || '-'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Created:</span>
                <span className="detail-value">
                  {selectedFeedback.created_at ? new Date(selectedFeedback.created_at).toLocaleString() : '-'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Closed Date:</span>
                <span className="detail-value">
                  {selectedFeedback.closed_date ? new Date(selectedFeedback.closed_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    timeZone: 'UTC'
                  }) : '-'}
                </span>
              </div>
            </div>

            <div className="detail-item" style={{ marginTop: '20px' }}>
              <span className="detail-label">Description:</span>
              <div className="detail-description">
                {selectedFeedback.description || 'No description provided'}
              </div>
            </div>

            {/* Status Update Section */}
            <div className="status-update-section" style={{ marginTop: '24px', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
              <h3 style={{ marginBottom: '16px' }}>Update Status</h3>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <label style={{ fontWeight: '500' }}>Current Status:</label>
                <span className={`status-badge ${(selectedFeedback.status || 'open').toLowerCase()}`}>
                  {(selectedFeedback.status || 'open').charAt(0).toUpperCase() + (selectedFeedback.status || 'open').slice(1).replace('_', ' ')}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginTop: '16px' }}>
                <label style={{ fontWeight: '500' }}>New Status:</label>
                <select 
                  value={tempStatus || selectedFeedback.status || 'open'} 
                  onChange={(e) => setTempStatus(e.target.value)}
                  style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ccc', minWidth: '150px' }}
                >
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                </select>
                <button 
                  className="view-workflow-btn" 
                  onClick={() => {
                    const newStatus = tempStatus || selectedFeedback.status || 'open';
                    const closedDate = newStatus === 'resolved' ? new Date().toISOString() : undefined;
                    updateFeedbackStatus(selectedFeedback.id, newStatus, closedDate);
                  }}
                  disabled={updatingFeedback === selectedFeedback.id}
                  style={{ 
                    background: updatingFeedback === selectedFeedback.id ? '#6c757d' : '#28a745', 
                    minWidth: '100px',
                    opacity: updatingFeedback === selectedFeedback.id ? 0.6 : 1
                  }}
                >
                  {updatingFeedback === selectedFeedback.id ? 'Updating...' : 'Update Status'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Feedback Page
  const renderFeedback = () => {
    // If a feedback item is selected, show the detail view
    if (selectedFeedback) {
      return renderFeedbackDetail();
    }
    // Apply client-side filters
    const { role, status, category, search, startDate, endDate } = feedbackFilters;
    const startTs = startDate ? new Date(startDate).getTime() : undefined;
    const endTs = endDate ? new Date(endDate).getTime() : undefined;

    const filtered = feedback.filter(item => {
      if (role !== 'all' && (item.submitter_role || 'user') !== role) return false;
      if (status !== 'all' && (item.status || 'open') !== status) return false;
      if (category !== 'all' && (item.category || '') !== category) return false;
      if (search) {
        const hay = `${item.submitter_name || ''} ${item.category || ''} ${item.description || ''} ${item.app_identifier || ''}`.toLowerCase();
        if (!hay.includes(search.toLowerCase())) return false;
      }
      if (startTs && item.created_at) {
        const t = new Date(item.created_at).getTime();
        if (t < startTs) return false;
      }
      if (endTs && item.created_at) {
        const t = new Date(item.created_at).getTime();
        if (t > endTs) return false;
      }
      return true;
    });

    const categories = Array.from(new Set(feedback.map(f => f.category).filter(Boolean))) as string[];

    return (
      <div className="monitoring-section">
        <div className="section-header">
          <div className="section-title">User/Doctor Feedback</div>
          <button 
            className={`refresh-button ${isLoadingFeedback ? 'refreshing' : ''}`} 
            onClick={fetchFeedback} 
            disabled={isLoadingFeedback}
            title="Refresh Feedback"
          >
            {isLoadingFeedback ? '⏳' : '🔄'}
          </button>
        </div>

        {/* Filters */}
        <div className="filters" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <select value={feedbackFilters.role} onChange={e => setFeedbackFilters(prev => ({ ...prev, role: e.target.value as any }))}>
            <option value="all">All Roles</option>
            <option value="user">User</option>
            <option value="doctor">Doctor</option>
          </select>
          <select value={feedbackFilters.status} onChange={e => setFeedbackFilters(prev => ({ ...prev, status: e.target.value as any }))}>
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
          </select>
          <select value={feedbackFilters.category} onChange={e => setFeedbackFilters(prev => ({ ...prev, category: e.target.value }))}>
            <option value="all">All Categories</option>
            {categories.map((c, idx) => (
              <option key={idx} value={c}>{c}</option>
            ))}
          </select>
          <input
            type="date"
            value={feedbackFilters.startDate}
            onChange={e => setFeedbackFilters(prev => ({ ...prev, startDate: e.target.value }))}
            placeholder="Start date"
          />
          <input
            type="date"
            value={feedbackFilters.endDate}
            onChange={e => setFeedbackFilters(prev => ({ ...prev, endDate: e.target.value }))}
            placeholder="End date"
          />
          <input
            type="text"
            placeholder="Search..."
            value={feedbackFilters.search}
            onChange={e => setFeedbackFilters(prev => ({ ...prev, search: e.target.value }))}
            style={{ flex: 1, minWidth: 200 }}
          />
        </div>

        {/* Feedback Table */}
        <div className="workflow-table">
          <div className="table-header">
            <div>Created</div>
            <div>Submitter</div>
            <div>Role</div>
            <div>Category</div>
            <div>Description</div>
            <div>Status</div>
            <div>Closed Date</div>
            <div>App</div>
            <div>Actions</div>
          </div>
          {filtered.slice(0, 100).map((fb, idx) => (
            <div key={idx} className="table-row">
              <div>{fb.created_at ? new Date(fb.created_at).toLocaleString() : '-'}</div>
              <div>{fb.submitter_name || (fb.user_id ?? '-')}</div>
              <div>
                <span className={`status-badge ${fb.submitter_role === 'doctor' ? 'pending' : 'success'}`}>
                  {fb.submitter_role || 'user'}
                </span>
              </div>
              <div>{fb.category || '-'}</div>
              <div className="message-preview" title={fb.description || ''}>
                {(fb.description || '').length > 60 ? `${(fb.description || '').slice(0, 60)}...` : (fb.description || '')}
              </div>
              <div>
                <span className={`status-badge ${(fb.status || 'open').toLowerCase()}`}>
                  {(fb.status || 'open').charAt(0).toUpperCase() + (fb.status || 'open').slice(1).replace('_', ' ')}
                </span>
              </div>
              <div>
                {(() => {
                  console.log('Rendering closed_date for feedback:', fb.id, 'closed_date:', fb.closed_date);
                  if (fb.closed_date) {
                    try {
                      const date = new Date(fb.closed_date);
                      // Use toLocaleDateString with timezone options for better display
                      return date.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        timeZone: 'UTC'
                      });
                    } catch (e) {
                      console.error('Error parsing closed_date:', fb.closed_date, e);
                      return fb.closed_date;
                    }
                  }
                  return '-';
                })()}
              </div>
              <div>{fb.app_identifier || '-'}</div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button 
                  className="view-workflow-btn" 
                  onClick={() => {
                    setSelectedFeedback(fb);
                    setTempStatus(fb.status || 'open');
                  }}
                  style={{ background: '#1e88e5', minWidth: '80px' }}
                  title="View full details and update status"
                >
                  Open
                </button>
                <button 
                  className="view-workflow-btn" 
                  onClick={() => viewScreenshot(fb.id)}
                  style={{ minWidth: '100px' }}
                >
                  Screenshot
                </button>
                <button 
                  className="view-workflow-btn" 
                  onClick={() => deleteFeedback(fb.id)}
                  style={{ background: '#dc3545', minWidth: '80px' }}
                  title="Delete feedback and screenshot"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="table-row"><div>No feedback found.</div></div>
          )}
        </div>
      </div>
    );
  };

  const renderUsers = () => {
    return (
      <div className="monitoring-section users-table">
        <div className="section-header">
          <div className="section-title">Users</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button 
              className={`refresh-button ${usersLoading ? 'refreshing' : ''}`} 
              onClick={fetchUsers} 
              disabled={usersLoading}
              title="Refresh Users"
            >
              {usersLoading ? '⏳' : '🔄'}
            </button>
            <button 
              className="view-workflow-btn" 
              style={{ background: '#ff9800' }} 
              onClick={markUsersForDeletion} 
              disabled={selectedUserIds.size === 0}
              title="Deactivate accounts but keep data"
            >
              Mark for Deletion
            </button>
            <button 
              className="view-workflow-btn" 
              style={{ background: '#dc3545' }} 
              onClick={permanentlyDeleteUsers} 
              disabled={selectedUserIds.size === 0}
              title="⚠️ Permanently delete users and ALL data (irreversible)"
            >
              Delete Permanently
            </button>
          </div>
        </div>

        <div className="workflow-table">
          <div className="table-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={selectAllUsers} onChange={toggleSelectAllUsers} />
              <span>Select All</span>
            </div>
            <div>ID</div>
            <div>Email</div>
            <div>Name</div>
            <div>Onboarding Status</div>
            <div>Last Login</div>
            <div>Login Count</div>
            <div>App Version</div>
            <div>Platform</div>
            <div>Delete Date</div>
            <div>Created</div>
          </div>
          {users.map((u: any) => (
            <div key={u.id} className="table-row">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input type="checkbox" checked={selectedUserIds.has(u.id)} onChange={() => toggleSelectUser(u.id)} />
              </div>
              <div>{u.id}</div>
              <div>{u.email}</div>
              <div>{u.full_name || [u.first_name, u.middle_name, u.last_name].filter(Boolean).join(' ') || '-'}</div>
              <div>
                <span className={`status-badge ${getUserStatusClass(u.onboarding_status)}`}>{getUserStatusLabel(u.onboarding_status)}</span>
                {u.is_tobe_deleted && <span className="status-badge error" style={{ marginLeft: 6 }}>to be deleted</span>}
              </div>
              <div>{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : 'Never'}</div>
              <div>{u.login_count || 0}</div>
              <div>{u.app_version || '-'}</div>
              <div>
                {u.platform ? (
                  <span className={`status-badge ${u.platform === 'iOS' ? 'success' : 'pending'}`} style={{ 
                    background: u.platform === 'iOS' ? '#e3f2fd' : '#fff3e0',
                    color: u.platform === 'iOS' ? '#1976d2' : '#f57c00',
                    border: `1px solid ${u.platform === 'iOS' ? '#90caf9' : '#ffb74d'}`
                  }}>
                    {u.platform}
                  </span>
                ) : '-'}
              </div>
              <div>{u.delete_date ? new Date(u.delete_date).toLocaleString() : '-'}</div>
              <div>{u.created_at ? new Date(u.created_at).toLocaleString() : '-'}</div>
            </div>
          ))}
          {users.length === 0 && (
            <div className="table-row"><div>No users found.</div></div>
          )}
        </div>
      </div>
    );
  };

  const renderDoctors = () => {
    return (
      <div className="monitoring-section doctors-table">
        <div className="section-header">
          <div className="section-title">Doctors</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button 
              className={`refresh-button ${doctorsLoading ? 'refreshing' : ''}`} 
              onClick={fetchDoctors} 
              disabled={doctorsLoading}
              title="Refresh Doctors"
            >
              {doctorsLoading ? '⏳' : '🔄'}
            </button>
            <button 
              className="view-workflow-btn" 
              style={{ background: '#ff9800' }} 
              onClick={markDoctorsForDeletion} 
              disabled={selectedDoctorIds.size === 0}
              title="Deactivate accounts but keep data"
            >
              Mark for Deletion
            </button>
            <button 
              className="view-workflow-btn" 
              style={{ background: '#dc3545' }} 
              onClick={permanentlyDeleteDoctors} 
              disabled={selectedDoctorIds.size === 0}
              title="⚠️ Permanently delete doctors and ALL data (irreversible)"
            >
              Delete Permanently
            </button>
          </div>
        </div>

        <div className="workflow-table">
          <div className="table-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={selectAllDoctors} onChange={toggleSelectAllDoctors} />
              <span>Select All</span>
            </div>
            <div>ID</div>
            <div>Email</div>
            <div>Name</div>
            <div>Verification</div>
            <div>Status</div>
            <div>Last Login</div>
            <div>Delete Date</div>
            <div>Created</div>
            <div>Actions</div>
          </div>
          {doctors.map((d: any) => (
            <div key={d.id} className="table-row">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input type="checkbox" checked={selectedDoctorIds.has(d.id)} onChange={() => toggleSelectDoctor(d.id)} />
              </div>
              <div>{d.id}</div>
              <div>{d.email}</div>
              <div>{d.full_name || '-'}</div>
              <div>
                {(() => {
                  const vs = (d.onboarding_status || '').toLowerCase();
                  const vsLabel =
                    vs === 'approved'
                      ? 'Approved'
                      : vs === 'submitted'
                        ? 'Submitted'
                        : vs === 'rejected'
                          ? 'Rejected'
                          : vs === 'onboarding_incomplete'
                            ? 'Onboarding Incomplete'
                            : vs === 'pending_email'
                              ? 'Pending Email'
                              : 'Pending';
                  const vsCls =
                    vs === 'approved'
                      ? 'success'
                      : vs === 'rejected'
                        ? 'error'
                        : vs === 'submitted'
                          ? 'in_progress'
                          : 'pending';
                  return <span className={`status-badge ${vsCls}`}>{vsLabel}</span>;
                })()}
              </div>
              <div>
                <span className={`status-badge ${d.is_active ? 'success' : 'pending'}`}>{d.is_active ? 'active' : 'inactive'}</span>
                {d.is_tobe_deleted && <span className="status-badge error" style={{ marginLeft: 6 }}>to be deleted</span>}
              </div>
              <div>{d.last_login_at ? new Date(d.last_login_at).toLocaleString() : 'Never'}</div>
              <div>{d.delete_date ? new Date(d.delete_date).toLocaleString() : '-'}</div>
              <div>{d.created_at ? new Date(d.created_at).toLocaleString() : '-'}</div>
              <div>
                {(() => {
                  const vs = (d.onboarding_status || '').toLowerCase();
                  if (vs === 'submitted' || vs === 'rejected') {
                    return (
                      <button className="view-workflow-btn" style={{ minWidth: '90px' }} onClick={() => openDoctorReview(d.id)}>Review</button>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
          ))}
          {doctors.length === 0 && (
            <div className="table-row"><div>No doctors found.</div></div>
          )}
        </div>
      </div>
    );
  };

  const renderDoctorReviewPage = () => {
    if (!reviewDoctorId) return null;
    return (
      <div className="monitoring-section" ref={reviewPanelRef}>
        <div className="section-header" style={{alignItems: 'center'}}>
          <div className="section-title">Doctor Review (ID: {reviewDoctorId})</div>
          <div style={{display: 'flex', gap: 8}}>
            <button className="view-workflow-btn" style={{ background: '#6c757d' }} onClick={() => { setActiveTab('doctors'); setReviewDoctorId(null); setReviewDetails(null); }}>Back</button>
          </div>
        </div>
        {reviewLoading && <div>Loading...</div>}
        {reviewDetails && (
          <>
            {/* Centered header: photo, name, phone, email, address, specialization */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 6, marginBottom: 8 }}>
              <div style={{ width: 256, height: 256, borderRadius: 12, overflow: 'hidden', background: '#eee' }}>
                {reviewDetails.profile_photo_url ? (
                  <img src={(reviewDetails.profile_photo_url || '').startsWith('http') ? reviewDetails.profile_photo_url : apiUrl(reviewDetails.profile_photo_url || '')} alt="profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>No Photo</div>
                )}
              </div>
              <div style={{ fontSize: 18, fontWeight: 600 }}>{reviewDetails.full_name || '-'}</div>
              <div>
                {(() => {
                  const dial = (reviewDetails.country_dial_code || '').toString();
                  const dialFmt = dial ? (dial.startsWith('+') ? dial : `+${dial}`) : '';
                  const phone = reviewDetails.contact_number || '';
                  return (dialFmt || phone) ? `${dialFmt}${dialFmt && phone ? ' ' : ''}${phone}` : '-';
                })()}
              </div>
              {reviewDetails.email && (
                <div style={{ color: '#555' }}>{reviewDetails.email}</div>
              )}
              {(reviewDetails.city || reviewDetails.state || reviewDetails.country_name) && (
                <div style={{ color: '#555' }}>
                  {[reviewDetails.city, reviewDetails.state, reviewDetails.country_name].filter(Boolean).join(', ')}
                </div>
              )}
              <div style={{ color: '#666' }}>
                {(reviewDetails.specialization || '-')}{reviewDetails.subspecialization ? ` • ${reviewDetails.subspecialization}` : ''}
              </div>
            </div>
            <div className="endpoints-table">
              <div className="table-header"><div>Section</div><div>Summary</div></div>
              <div className="table-row"><div>Profile</div><div>{reviewDetails.full_name || '-'}</div></div>
              <div className="table-row"><div>Education</div><div>{(reviewDetails.education || []).length}</div></div>
              <div className="table-row"><div>Practices</div><div>{(reviewDetails.practices || []).length}</div></div>
              <div className="table-row"><div>Documents</div><div>{(reviewDetails.documents || []).length}</div></div>
              <div className="table-row"><div>Services</div><div>{(reviewDetails.services || []).length}</div></div>
              <div className="table-row"><div>Bank</div><div>{reviewDetails.bank_details ? 'Present' : 'Missing'}</div></div>
            </div>

            {((reviewDetails.documents || []).filter((d: any) => d.document_type !== 'education_certificate' && d.document_type !== 'certification_document')).length > 0 && (
              <div style={{ marginTop: 8 }}>
                <div className="section-title">Documents</div>
                <div className="endpoints-table">
                  <div className="table-header"><div>Type</div><div>URL</div><div>Action</div></div>
                  {(reviewDetails.documents || []).filter((doc: any) => doc.document_type !== 'education_certificate' && doc.document_type !== 'certification_document').map((doc: any, idx: number) => (
                    <div className="table-row" key={idx}>
                      <div>{doc.document_type}</div>
                      <div className="message-preview" title={(doc.download_url || doc.document_url) || ''}>{(doc.download_url || doc.document_url) || ''}</div>
                      <div>
                        <button className="view-workflow-btn" onClick={() => { const raw = (doc.download_url || doc.document_url) || ''; const href = raw.startsWith('http') ? raw : apiUrl(raw); window.open(href, '_blank'); }}>Open</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginTop: 16 }}>
              <div className="section-title">Profile Details</div>
              <div className="endpoints-table">
                <div className="table-header"><div>Field</div><div>Value</div></div>
                <div className="table-row"><div>Name</div><div>{reviewDetails.full_name || '-'}</div></div>
                <div className="table-row"><div>Email</div><div>{reviewDetails.email || '-'}</div></div>
                <div className="table-row"><div>Contact</div><div>{(reviewDetails.country_dial_code ? `+${reviewDetails.country_dial_code} ` : '')}{reviewDetails.contact_number || '-'}</div></div>
                {reviewDetails.alternate_contact_number && (
                  <div className="table-row"><div>Alt. Contact</div><div>{reviewDetails.alternate_contact_number}</div></div>
                )}
                <div className="table-row"><div>DOB</div><div>{reviewDetails.date_of_birth ? new Date(reviewDetails.date_of_birth).toLocaleDateString() : '-'}</div></div>
                <div className="table-row"><div>Gender</div><div>{reviewDetails.gender || '-'}</div></div>
                <div className="table-row"><div>Registration No.</div><div>{reviewDetails.registration_number || '-'}</div></div>
                <div className="table-row"><div>Registration Council</div><div>{reviewDetails.registration_council || '-'}</div></div>
                <div className="table-row"><div>Registration Year</div><div>{reviewDetails.registration_year ?? '-'}</div></div>
                <div className="table-row"><div>Specialization</div><div>{reviewDetails.specialization || '-'}</div></div>
                <div className="table-row"><div>Subspecialization</div><div>{reviewDetails.subspecialization || '-'}</div></div>
                <div className="table-row"><div>Experience (years)</div><div>{reviewDetails.years_experience ?? '-'}</div></div>
                <div className="table-row"><div>Bio</div><div>{reviewDetails.bio || '-'}</div></div>
                <div className="table-row"><div>Address</div><div>{[reviewDetails.address_line1, reviewDetails.address_line2, reviewDetails.city, reviewDetails.state, reviewDetails.country, reviewDetails.pincode].filter(Boolean).join(', ') || '-'}</div></div>
                {reviewDetails.country_name && (
                  <div className="table-row"><div>Country</div><div>{reviewDetails.country_name} ({reviewDetails.country_iso2 || 'NA'})</div></div>
                )}
                {(reviewDetails.languages_spoken || []).length > 0 && (
                  <div className="table-row"><div>Languages</div><div>{(reviewDetails.languages_spoken || []).join(', ')}</div></div>
                )}
                {(reviewDetails.timezone_display_name || reviewDetails.timezone_identifier) && (
                  <div className="table-row"><div>Timezone</div><div>{reviewDetails.timezone_display_name || reviewDetails.timezone_identifier} {reviewDetails.timezone_utc_offset ? `(${reviewDetails.timezone_utc_offset})` : ''}</div></div>
                )}
                {typeof reviewDetails.rating === 'number' && (
                  <div className="table-row"><div>Rating</div><div>{reviewDetails.rating}</div></div>
                )}
                {typeof reviewDetails.total_consultations === 'number' && (
                  <div className="table-row"><div>Total Consultations</div><div>{reviewDetails.total_consultations}</div></div>
                )}
              </div>
            </div>

            {(reviewDetails.education || []).length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div className="section-title">Education</div>
                <div className="endpoints-table">
                  <div className="table-header"><div>Degree</div><div>Institution</div><div>Year</div><div>Document</div></div>
                  {(reviewDetails.education || []).map((ed: any, idx: number) => (
                    <div className="table-row" key={idx}>
                      <div>{ed.degree}</div>
                      <div>{ed.institution}</div>
                      <div>{ed.year_of_completion}</div>
                      <div>
                        {ed.download_url ? (
                          <button className="view-workflow-btn" onClick={() => { const href = ed.download_url.startsWith('http') ? ed.download_url : apiUrl(ed.download_url); window.open(href, '_blank'); }}>Open</button>
                        ) : (
                          <span>-</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(reviewDetails.practices || []).length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div className="section-title">Practices</div>
                <div className="endpoints-table">
                  <div className="table-header"><div>Name</div><div>Type</div><div>Address</div><div>City</div><div>Contact</div><div>Timings</div></div>
                  {(reviewDetails.practices || []).map((p: any, idx: number) => (
                    <div key={idx}>
                      <div className="table-row">
                        <div>{p.practice_name}</div>
                        <div>{p.practice_type}</div>
                        <div>{p.address}</div>
                        <div>{p.city}</div>
                        <div>{p.contact_number || '-'}</div>
                        <div>{(p.timings || []).length || p.timings_count || 0}</div>
                      </div>
                      {(p.timings || []).length > 0 && (
                        <div style={{ margin: '8px 0 16px 0', padding: '8px 12px', background: '#fff', border: '1px solid #eee', borderRadius: 6 }}>
                          <div className="endpoints-table">
                            <div className="table-header"><div>Day</div><div>Available</div><div>Start</div><div>End</div><div>Slot (min)</div><div>Break</div></div>
                            {(p.timings || []).map((t: any, ti: number) => (
                              <div className="table-row" key={ti}>
                                <div style={{ textTransform: 'capitalize' }}>{t.day_of_week}</div>
                                <div>{t.is_available ? 'Yes' : 'No'}</div>
                                <div>{t.start_time ? new Date(t.start_time).toLocaleTimeString() : '-'}</div>
                                <div>{t.end_time ? new Date(t.end_time).toLocaleTimeString() : '-'}</div>
                                <div>{t.slot_duration}</div>
                                <div>{t.break_start_time && t.break_end_time ? `${new Date(t.break_start_time).toLocaleTimeString()} - ${new Date(t.break_end_time).toLocaleTimeString()}` : '-'}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(reviewDetails.certifications || []).length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div className="section-title">Certifications</div>
                <div className="endpoints-table">
                  <div className="table-header"><div>Name</div><div>Organization</div><div>Issue</div><div>Expiry</div><div>Document</div></div>
                  {(reviewDetails.certifications || []).map((c: any, idx: number) => {
                    const href = c && c.download_url ? (c.download_url.startsWith('http') ? c.download_url : apiUrl(c.download_url)) : null;
                    return (
                      <div className="table-row" key={idx}>
                        <div>{c.certification_name}</div>
                        <div>{c.issuing_organization}</div>
                        <div>{c.issue_date ? new Date(c.issue_date).toLocaleDateString() : '-'}</div>
                        <div>{c.expiry_date ? new Date(c.expiry_date).toLocaleDateString() : '-'}</div>
                        <div>{href ? <button className="view-workflow-btn" onClick={() => window.open(href, '_blank')}>Open</button> : <span>-</span>}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {(reviewDetails.services || []).length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div className="section-title">Services</div>
                <div className="endpoints-table">
                  <div className="table-header"><div>Service</div><div>Fee</div><div>Duration (min)</div></div>
                  {(reviewDetails.services || []).map((s: any, idx: number) => (
                    <div className="table-row" key={idx}>
                      <div>{s.service_name}</div>
                      <div>{typeof s.fee === 'number' ? `₹${s.fee.toFixed(2)}` : s.fee}</div>
                      <div>{s.duration_minutes}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {reviewDetails.bank_details && (
              <div style={{ marginTop: 16 }}>
                <div className="section-title">Bank Details</div>
                <div className="endpoints-table">
                  <div className="table-header"><div>Field</div><div>Value</div></div>
                  <div className="table-row">
                    <div>Account Holder Name</div>
                    <div>{reviewDetails.bank_details.account_holder_name || '-'}</div>
                  </div>
                  <div className="table-row">
                    <div>Bank Name</div>
                    <div>{reviewDetails.bank_details.bank_name || '-'}</div>
                  </div>
                  <div className="table-row">
                    <div>Account Number</div>
                    <div>{reviewDetails.bank_details.account_number_masked || '-'}</div>
                  </div>
                  <div className="table-row">
                    <div>IFSC Code</div>
                    <div>{reviewDetails.bank_details.ifsc_code || '-'}</div>
                  </div>
                  <div className="table-row">
                    <div>Branch Name</div>
                    <div>{reviewDetails.bank_details.branch_name || '-'}</div>
                  </div>
                  <div className="table-row">
                    <div>Account Type</div>
                    <div>{reviewDetails.bank_details.account_type ? reviewDetails.bank_details.account_type.charAt(0).toUpperCase() + reviewDetails.bank_details.account_type.slice(1) : '-'}</div>
                  </div>
                  {reviewDetails.bank_details.upi_id && (
                    <div className="table-row">
                      <div>UPI ID</div>
                      <div>{reviewDetails.bank_details.upi_id}</div>
                    </div>
                  )}
                  {reviewDetails.bank_details.pan_number && (
                    <div className="table-row">
                      <div>PAN Number</div>
                      <div>{reviewDetails.bank_details.pan_number}</div>
                    </div>
                  )}
                  {reviewDetails.bank_details.gst_number && (
                    <div className="table-row">
                      <div>GST Number</div>
                      <div>{reviewDetails.bank_details.gst_number}</div>
                    </div>
                  )}
                  <div className="table-row">
                    <div>Verified</div>
                    <div>{reviewDetails.bank_details.is_verified ? 'Yes' : 'No'}</div>
                  </div>
                </div>
              </div>
            )}

            <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input type="text" placeholder="Internal notes (optional)" value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} style={{ padding: 8, minWidth: 240 }} />
              <input type="text" placeholder="Feedback to doctor (for reject/request-info)" value={reviewFeedback} onChange={(e) => setReviewFeedback(e.target.value)} style={{ padding: 8, minWidth: 320 }} />
              <button className="view-workflow-btn" style={{ background: '#28a745' }} disabled={reviewActionLoading !== null} onClick={approveDoctor}>Approve</button>
              <button className="view-workflow-btn" style={{ background: '#ff9800' }} disabled={reviewActionLoading !== null} onClick={requestInfoDoctor}>Request Info</button>
              <button className="view-workflow-btn" style={{ background: '#dc3545' }} disabled={reviewActionLoading !== null} onClick={rejectDoctor}>Reject</button>
            </div>
          </>
        )}
      </div>
    );
  };





  // Password Reset component (if token is present in URL)
  if (showPasswordReset && resetToken) {
    return (
      <AdminPasswordReset 
        token={resetToken} 
        onSuccess={() => {
          setShowPasswordReset(false);
          // Clear token from URL
          window.history.replaceState({}, document.title, window.location.pathname);
        }} 
      />
    );
  }

  // Login component
  if (!auth.isAuthenticated) {
    return (
      <div className="admin-login-page">
        {/* Header - Reusing website header component */}
        <Header showDietitiansLink={false} />

        {/* Login Form */}
        <div className="login-container">
          <div className="login-form">
            {/* Logo */}
            <div className="login-logo-container">
              <img src="/images/zivo_logo.png" alt="ZivoHealth Logo" className="login-logo-img" />
            </div>
            
            <h2>Admin Dashboard</h2>
            <p>Please sign in to access the admin panel.</p>
          
          {/* Password Login Mode */}
          {loginMode === 'password' && (
            <form onSubmit={login}>
              <div className="form-group">
                <label>Username:</label>
                <input
                  type="text"
                  value={loginForm.username}
                  onChange={(e) => setLoginForm({...loginForm, username: e.target.value})}
                  placeholder="Enter your username"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Password:</label>
                <input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                  placeholder="Enter your password"
                  required
                />
              </div>
              
              {loginError && (
                <div className="error-message">
                  {loginError}
                </div>
              )}
              
              <button type="submit" className="login-button">
                Login
              </button>

              <div className="login-options">
                <button type="button" className="login-option-button" onClick={() => setLoginMode('otp')}>
                  <span className="option-icon">💬</span>
                  Login with OTP
                </button>
                <button type="button" className="login-option-button" onClick={() => setLoginMode('forgot-password')}>
                  <span className="option-icon">↻</span>
                  Forgot Password
                </button>
              </div>
            </form>
          )}

          {/* OTP Email Entry Mode */}
          {loginMode === 'otp' && (
            <form onSubmit={(e) => {
              e.preventDefault();
              requestOtp(otpEmail);
            }}>
              <div className="form-group">
                <label>Email Address:</label>
                <input
                  type="email"
                  value={otpEmail}
                  onChange={(e) => setOtpEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  disabled={isRequestingOtp}
                />
              </div>
              
              {loginError && (
                <div className="error-message">
                  {loginError}
                </div>
              )}
              
              <button type="submit" className="login-button" disabled={isRequestingOtp}>
                {isRequestingOtp ? 'Sending...' : 'Send OTP'}
              </button>

              <div style={{ marginTop: 10 }}>
                <button type="button" className="login-button" style={{ background: '#6c757d' }} onClick={() => {
                  setLoginMode('password');
                  setOtpEmail('');
                  setLoginError('');
                }}>
                  Back to Password Login
                </button>
              </div>
            </form>
          )}

          {/* OTP Code Entry Mode */}
          {loginMode === 'otp-enter' && (
            <form onSubmit={(e) => {
              e.preventDefault();
              login(e);
            }}>
              <div className="demo-credentials" style={{ marginBottom: 15, background: '#d4edda', color: '#155724' }}>
                <p>✅ OTP has been sent to <strong>{otpEmail}</strong></p>
              </div>

              <div className="form-group">
                <label>One-Time Password (OTP):</label>
                <input
                  type="text"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  placeholder="Enter the 6-digit OTP"
                  required
                  maxLength={6}
                />
              </div>
              
              {loginError && (
                <div className="error-message">
                  {loginError}
                </div>
              )}
              
              <button type="submit" className="login-button">
                Verify OTP
              </button>

              <div style={{ marginTop: 10 }}>
                <button type="button" className="login-button" style={{ background: '#17a2b8' }} onClick={() => requestOtp(otpEmail)} disabled={isRequestingOtp}>
                  {isRequestingOtp ? 'Sending...' : 'Resend OTP'}
                </button>
              </div>

              <div style={{ marginTop: 10 }}>
                <button type="button" className="login-button" style={{ background: '#6c757d' }} onClick={() => {
                  setLoginMode('password');
                  setOtpEmail('');
                  setOtpCode('');
                  setLoginError('');
                }}>
                  Back to Password Login
                </button>
              </div>
            </form>
          )}

          {/* Forgot Password Mode */}
          {loginMode === 'forgot-password' && (
            <form onSubmit={(e) => {
              e.preventDefault();
              requestPasswordReset(forgotPasswordEmail);
            }}>
              <div className="form-group">
                <label>Email Address:</label>
                <input
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  disabled={isRequestingPasswordReset}
                />
              </div>
              
              {loginError && (
                <div className="error-message">
                  {loginError}
                </div>
              )}

              {forgotPasswordMessage && (
                <div className="demo-credentials" style={{ background: '#d4edda', color: '#155724' }}>
                  <p>{forgotPasswordMessage}</p>
                </div>
              )}
              
              <button 
                type="submit" 
                className="login-button"
                disabled={isRequestingPasswordReset}
              >
                {isRequestingPasswordReset ? 'Sending...' : 'Send Reset Link'}
              </button>

              <div style={{ marginTop: 10 }}>
                <button type="button" className="login-button" style={{ background: '#6c757d' }} onClick={() => {
                  setLoginMode('password');
                  setForgotPasswordEmail('');
                  setForgotPasswordMessage('');
                  setLoginError('');
                }}>
                  Back to Password Login
                </button>
              </div>
            </form>
          )}
          
        </div>
        </div>

        {/* Footer - Full website footer (logo, tagline, social, platform, newsletter) */}
        <Footer variant="full" />
      </div>
    );
  }

  // Main dashboard
  return (
    <div className="App">
      <Header
        title="ZivoHealth Monitoring Dashboard"
        healthStatus={systemHealth ? {
          status: systemHealth.status as 'healthy' | 'degraded' | 'unhealthy',
          health_score: systemHealth.health_score
        } : undefined}
        userName={auth.user?.username || 'User'}
        onLogout={logout}
      />

      {globalNotice && (
        <div
          style={{
            margin: '10px 16px 0 16px',
            padding: '10px 12px',
            borderRadius: 8,
            background:
              globalNotice.type === 'service'
                ? '#fef3c7'
                : globalNotice.type === 'session'
                  ? '#fee2e2'
                  : '#e0f2fe',
            color:
              globalNotice.type === 'service'
                ? '#92400e'
                : globalNotice.type === 'session'
                  ? '#991b1b'
                  : '#075985',
            border:
              globalNotice.type === 'service'
                ? '1px solid #fde68a'
                : globalNotice.type === 'session'
                  ? '1px solid #fecaca'
                  : '1px solid #bae6fd',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <span>{globalNotice.message}</span>
          <button
            type="button"
            onClick={() => setGlobalNotice(null)}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontWeight: 600,
              color: 'inherit',
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="app-body">
        {/* Left Navigation */}
        <nav className="left-nav">
          <div className="nav-items">
            <button 
              className={`nav-item ${activeTab === 'system' ? 'active' : ''}`}
              onClick={() => setActiveTab('system')}
            >
              <span className="nav-icon">🖥️</span>
              <span className="nav-label">System Monitoring</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'app' ? 'active' : ''}`}
              onClick={() => setActiveTab('app')}
            >
              <span className="nav-icon">📊</span>
              <span className="nav-label">App Monitoring</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'feedback' ? 'active' : ''}`}
              onClick={() => setActiveTab('feedback')}
            >
              <span className="nav-icon">🖼️</span>
              <span className="nav-label">Feedback</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => setActiveTab('users')}
            >
              <span className="nav-icon">👥</span>
              <span className="nav-label">Users</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'doctors' ? 'active' : ''}`}
              onClick={() => setActiveTab('doctors')}
            >
              <span className="nav-icon">🩺</span>
              <span className="nav-label">Doctors</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'healthscore' ? 'active' : ''}`}
              onClick={() => setActiveTab('healthscore')}
            >
              <span className="nav-icon">❤️</span>
              <span className="nav-label">Health Score Ops</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'db-monitoring' ? 'active' : ''}`}
              onClick={() => setActiveTab('db-monitoring')}
            >
              <span className="nav-icon">🗄️</span>
              <span className="nav-label">DB Monitoring</span>
            </button>
            {(
              <button 
                className={`nav-item ${activeTab === 'admins' ? 'active' : ''}`}
                onClick={() => setActiveTab('admins')}
              >
                <span className="nav-icon">🔐</span>
                <span className="nav-label">Admin Management</span>
              </button>
            )}
            <button 
              className={`nav-item ${activeTab === 'holidays' ? 'active' : ''}`}
              onClick={() => setActiveTab('holidays')}
            >
              <span className="nav-icon">📅</span>
              <span className="nav-label">System Holidays</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'versions' ? 'active' : ''}`}
              onClick={() => setActiveTab('versions')}
            >
              <span className="nav-icon">📦</span>
              <span className="nav-label">Version Management</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'scheduled-jobs' ? 'active' : ''}`}
              onClick={() => setActiveTab('scheduled-jobs')}
            >
              <span className="nav-icon">⏰</span>
              <span className="nav-label">Scheduled Jobs</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'llm-pricing' ? 'active' : ''}`}
              onClick={() => setActiveTab('llm-pricing')}
            >
              <span className="nav-icon">💰</span>
              <span className="nav-label">LLM Pricing</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'llm-usage' ? 'active' : ''}`}
              onClick={() => setActiveTab('llm-usage')}
            >
              <span className="nav-icon">📈</span>
              <span className="nav-label">LLM Usage</span>
            </button>
            {/* Doctor Verification handled within Doctors tab (no separate tab) */}
          </div>
        </nav>

        <main className="app-main">
          {activeTab === 'system' && renderSystemMonitoring()}
          {activeTab === 'app' && renderAppMonitoring()}
          {activeTab === 'feedback' && renderFeedback()}
          {activeTab === 'users' && renderUsers()}
          {activeTab === 'doctors' && renderDoctors()}
          {activeTab === 'doctor-review' && renderDoctorReviewPage()}
          {activeTab === 'healthscore' && <HealthScoreOps safeFetch={safeFetch} />}
          {activeTab === 'db-monitoring' && <DatabaseMonitoring safeFetch={safeFetch} />}
          {activeTab === 'admins' && <AdminManagement apiUrl={apiUrl} getAuthHeaders={getAuthHeaders} safeFetch={safeFetch} />}
          {activeTab === 'holidays' && <SystemHolidays apiUrl={apiUrl} getAuthHeaders={getAuthHeaders} safeFetch={safeFetch} />}
          {activeTab === 'versions' && <VersionManagement apiUrl={apiUrl} getAuthHeaders={getAuthHeaders} safeFetch={safeFetch} />}
          {activeTab === 'scheduled-jobs' && <ScheduledJobs apiUrl={apiUrl} getAuthHeaders={getAuthHeaders} safeFetch={safeFetch} />}
          {activeTab === 'llm-pricing' && <LLMPricing apiUrl={apiUrl} getAuthHeaders={getAuthHeaders} safeFetch={safeFetch} />}
          {activeTab === 'llm-usage' && <LLMUsage apiUrl={apiUrl} getAuthHeaders={getAuthHeaders} safeFetch={safeFetch} />}
        </main>
      </div>
    </div>
  );
}

export default App;
